// PIOCHER DANS L'ANNUAIRE : filtrer, puis cocher les personnes à inviter.
//
// Les mêmes filtres que l'onglet « Annuaire ». Une personne qui a demandé à ne
// plus être sollicitée reste visible (on sait pourquoi elle manque), mais ne se
// coche pas.
import { useState } from 'react';
import { personName, usePeople, type Person } from './api';
import { filterPeople, NO_FILTER, PeopleFilters, type PeopleFilter } from './PeopleFilters';
import { PersonChips } from './PeopleScreen';
import { Button, Spinner } from '../components/ui';
import { WriteAction } from '../write/Forms';
import { ImportPeople } from './ImportPeople';
import './people.css';

export function PeoplePicker({
  projectId,
  selected,
  onChange,
  max = 50,
}: {
  projectId: string;
  selected: string[];
  onChange: (ids: string[]) => void;
  max?: number;
}) {
  const query = usePeople(projectId);
  const [filter, setFilter] = useState<PeopleFilter>(NO_FILTER);
  if (query.isPending) return <Spinner label="Chargement de l’annuaire" />;
  const all = query.data?.people ?? [];
  if (all.length === 0) {
    // Annuaire vide : l'import est LE geste à faire, en bleu, sans quitter la page.
    return (
      <div className="ppl-picker-empty">
        <p className="zy-texte-muet">L’annuaire est vide : importez la liste de vos utilisateurs pour pouvoir y piocher.</p>
        <div className="ppl-picker-empty-actions">
          <WriteAction label="Importer une liste" title="Importer une liste de personnes" accent wide>
            {(done) => <ImportPeople projectId={projectId} onDone={done} />}
          </WriteAction>
        </div>
      </div>
    );
  }
  const shown = filterPeople(all, filter);
  const pickable = (p: Person) => !p.do_not_contact;
  const chosen = new Set(selected);
  const toggle = (id: string) =>
    onChange(chosen.has(id)
      ? selected.filter((x) => x !== id)
      // Une seule personne (rendez-vous d'un entretien) : cocher remplace.
      : max === 1 ? [id] : [...selected, id].slice(0, max));
  const shownPickable = shown.filter(pickable);
  const allShownChosen = shownPickable.length > 0 && shownPickable.every((p) => chosen.has(p.id));
  const noEmail = all.filter((p) => chosen.has(p.id) && !p.email).length;

  return (
    <div className="ppl-picker">
      <PeopleFilters
        value={filter}
        onChange={setFilter}
        pools={query.data?.pools ?? []}
        roles={query.data?.roles ?? []}
        people={all}
      />
      <div className="ppl-pick-bar">
        <span>
          <strong>{selected.length}</strong> personne{selected.length > 1 ? 's' : ''} choisie{selected.length > 1 ? 's' : ''}
          {noEmail ? ` · ${noEmail} sans e-mail (vous enverrez le lien vous-même)` : ''}
        </span>
        <span className="inv-row-actions">
          {max > 1 ? (
          <Button variant="link" size="s" className="u-push-end"
            type="button"
            disabled={shownPickable.length === 0}
            onClick={() => {
              const ids = shownPickable.map((p) => p.id);
              onChange(allShownChosen
                ? selected.filter((x) => !ids.includes(x))
                : [...new Set([...selected, ...ids])].slice(0, max));
            }}
          >
            {allShownChosen ? 'Décocher les personnes affichées' : `Cocher les ${shownPickable.length} affichées`}
          </Button>
          ) : null}
          {selected.length > 0 ? (
            <Button variant="link" size="s" className="u-push-end" type="button" onClick={() => onChange([])}>Tout décocher</Button>
          ) : null}
        </span>
      </div>
      {shown.length === 0 ? (
        <p className="zy-texte-muet">Rien ne correspond aux filtres.</p>
      ) : (
        <ul className="ppl-pick-list">
          {shown.map((p) => (
            <li key={p.id}>
              <label className={`ppl-pick${pickable(p) ? '' : ' ppl-pick-off'}`}>
                <input className="zy-check"
                  type="checkbox"
                  checked={chosen.has(p.id)}
                  disabled={!pickable(p)}
                  onChange={() => toggle(p.id)}
                />
                <span className="ppl-main">
                  <span className="ppl-name">{personName(p)}</span>
                  <span className="ppl-sub">
                    {p.do_not_contact
                      ? 'A demandé à ne plus être sollicitée'
                      : [p.job_title, p.role, p.email ?? 'sans e-mail'].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <PersonChips person={p} />
              </label>
            </li>
          ))}
        </ul>
      )}
      {max > 1 && selected.length >= max ? <p className="zy-texte-muet">{max} personnes au plus par envoi.</p> : null}
    </div>
  );
}
